import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Plus, Trash2, ScanLine } from "lucide-react";

export default function AdminAnimals() {
  const [items, setItems] = useState([]);
  const [lots, setLots] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    species: "goat", breed: "", age_months: 4, initial_weight_kg: 12,
    purchase_price: 8000, lot_id: "", notes: "",
  });
  const [showQR, setShowQR] = useState(null);

  const load = async () => {
    const [a, l] = await Promise.all([
      api.get("/admin/animals"),
      api.get("/admin/lots"),
    ]);
    setItems(a.data); setLots(l.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.breed) return toast.error("Enter breed");
    try {
      await api.post("/admin/animals", { ...form, lot_id: form.lot_id || null });
      toast.success("Animal added");
      setShowForm(false);
      setForm({ species: "goat", breed: "", age_months: 4, initial_weight_kg: 12, purchase_price: 8000, lot_id: "", notes: "" });
      load();
    } catch (_) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this animal?")) return;
    await api.delete(`/admin/animals/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl">Animals</h1>
        <button onClick={() => setShowForm(true)} data-testid="add-animal-btn"
          className="px-4 py-2 rounded-xl bg-[#D71921] text-white text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add animal
        </button>
      </div>

      <div className="gf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F4E9] text-xs uppercase tracking-wider text-[#6B5F58]">
            <tr>
              <th className="text-left px-4 py-3">Tag</th>
              <th className="text-left px-4 py-3">Species</th>
              <th className="text-left px-4 py-3">Breed</th>
              <th className="text-left px-4 py-3">Age</th>
              <th className="text-left px-4 py-3">Weight</th>
              <th className="text-left px-4 py-3">Lot</th>
              <th className="text-left px-4 py-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const lot = lots.find((l) => l.id === a.lot_id);
              return (
                <tr key={a.id} className="border-t border-[#E8E0D2]">
                  <td className="px-4 py-3 font-mono text-xs">{a.tag_number}</td>
                  <td className="px-4 py-3 capitalize">{a.species}</td>
                  <td className="px-4 py-3">{a.breed}</td>
                  <td className="px-4 py-3">{a.age_months}mo</td>
                  <td className="px-4 py-3">{a.current_weight_kg}kg</td>
                  <td className="px-4 py-3 text-xs">{lot ? lot.lot_code : "—"}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-[#FCEDED] text-[#D71921]">{a.health_status}</span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => setShowQR(a)} className="text-[#D71921] text-xs"><ScanLine className="w-4 h-4 inline" /></button>
                    <button onClick={() => remove(a.id)} className="text-red-600 text-xs"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-[#6B5F58]">No animals yet. Add the first one!</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 backdrop-blur-md bg-stone-900/40 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl border border-[#E8E0D2] max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl mb-4">Add animal</div>
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Species" value={form.species} onChange={(v) => setForm({...form, species: v})} options={[["goat","Goat"],["sheep","Sheep"]]} />
              <Inp label="Breed" value={form.breed} onChange={(v) => setForm({...form, breed: v})} testid="animal-breed" />
              <Inp label="Age (months)" type="number" value={form.age_months} onChange={(v) => setForm({...form, age_months: +v})} />
              <Inp label="Initial weight (kg)" type="number" value={form.initial_weight_kg} onChange={(v) => setForm({...form, initial_weight_kg: +v})} />
              <Inp label="Purchase price (₹)" type="number" value={form.purchase_price} onChange={(v) => setForm({...form, purchase_price: +v})} />
              <Sel label="Assign to lot" value={form.lot_id} onChange={(v) => setForm({...form, lot_id: v})}
                options={[["", "— none —"], ...lots.map(l => [l.id, `${l.lot_code} ${l.name}`])]} />
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={submit} data-testid="save-animal-btn" className="flex-1 px-4 py-2.5 rounded-xl bg-[#D71921] text-white font-medium">Save</button>
              <button onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-[#E8E0D2]">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showQR && (
        <div className="fixed inset-0 z-50 backdrop-blur-md bg-stone-900/40 grid place-items-center p-4" onClick={() => setShowQR(null)}>
          <div className="bg-white rounded-2xl border border-[#E8E0D2] max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-xl">{showQR.tag_number}</div>
            <div className="text-xs text-[#6B5F58] mb-4">{showQR.species} · {showQR.breed}</div>
            {showQR.qr_code_b64 && <img src={showQR.qr_code_b64} alt="QR" className="mx-auto w-56 h-56" />}
            <div className="text-xs text-[#6B5F58] mt-3">Scan to view animal record</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Inp({ label, value, onChange, type="text", testid }) {
  return (
    <label className="block">
      <span className="text-xs text-[#6B5F58]">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 outline-none focus:border-[#D71921]" />
    </label>
  );
}

function Sel({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-xs text-[#6B5F58]">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 outline-none focus:border-[#D71921] bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
